import { JSX } from "react";
import { ActiveLabel, Bar, BarChart, BarShapeProps, BaseTickContentProps, CartesianGrid, Rectangle, ResponsiveContainer, Tooltip, TooltipContentProps, XAxis, YAxis } from "recharts";
import { ValueType } from "recharts/types/component/DefaultTooltipContent";
import { BenchmarkingDashboardDetailsTypeEnum } from "@app/overviews/network-benchmarking-dashboard/dashboard-details";
import { EffectivenessAndEfficiencyData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { colors, widgetTooltipCursor } from "@app/overviews/network-benchmarking-dashboard/tiles/utility";
import { ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import DashboardTile from "@app/shared/qes-standards/DashboardTile";
import { capitalizeFirstLetter } from "@app/shared/utility/capitalizeFirstLetter";
import { useHoverBoundary } from "@app/shared/hooks/useHoverBoundary";

interface EffectivenessAndEfficiencyProps {
  chartType: "effectiveness" | "efficiency";
  customTooltip: (
    props: TooltipContentProps<ValueType, string | number>,
    titleKey: string,
    round?: number,
  ) => JSX.Element;
  customXAxisTick?: (props: BaseTickContentProps) => JSX.Element;
  data: EffectivenessAndEfficiencyData[];
  getTotalProviderCount: (contractId: string) => number;
  loading: boolean;
  onDashboardDetailsViewClick: (
    drillinOption: BenchmarkingDashboardDetailsTypeEnum,
    clickedCount: number,
    selectedVersionId?: number,
    networkOverlappingCount?: number,
    volumeLabel?: ClaimsActivityVolumeLabelEnum,
  ) => void;
  selectedNetwork: string;
}

const EffectivenessAndEfficiency = (props: EffectivenessAndEfficiencyProps): JSX.Element => {
  const { isHovered: isChartHovered, ref: chartRef } = useHoverBoundary<HTMLDivElement>();

  const onEffectivenessAndEfficiencyClick = (activeLabel: ActiveLabel): void => {
    // only allow to drill into the dashboard details view when
    // clicking in a bar section and the ability to drill into the dashboard is enabled
    if (!!activeLabel?.toString()) {
      const totalProviderCount = props.getTotalProviderCount(activeLabel.toString());
      const contractData: EffectivenessAndEfficiencyData = props.data.find(x => x.contractId === activeLabel.toString());

      if (!!contractData && !!totalProviderCount) {
        props.onDashboardDetailsViewClick(BenchmarkingDashboardDetailsTypeEnum.NetworkDrillin, totalProviderCount, contractData?.versionId);
      }
    }
  }

  return (
    <DashboardTile
      loading={props.loading}
      subtitle="Each bar can be clicked to reveal additional details."
      title={capitalizeFirstLetter(props.chartType)}
      widthClass="col-md-6"
    >
      <div
        ref={chartRef}
        style={{ height: "300px", width: "100%" }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            barCategoryGap="20%"
            width={500}
            height={300}
            data={props.data}
            margin={{
              bottom: 30,
              left: -30,
              right: 0,
              top: 20,
            }}
            onClick={(e) => { onEffectivenessAndEfficiencyClick(e.activeLabel) }}
          >
            <CartesianGrid horizontal={false} vertical={false} />
            <XAxis dataKey="contractId" type="category" tickLine={false} interval={0} tick={props.customXAxisTick} />
            <YAxis dataKey={props.chartType} type="number" domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} tickLine={false} />
            <Tooltip content={(customTooltipProps) => props.customTooltip(customTooltipProps, "contractId", 2)} cursor={widgetTooltipCursor} active={isChartHovered ? undefined : false} />
            <Bar
              dataKey={props.chartType}
              shape={(shapeProps: BarShapeProps) => {
                const entry = shapeProps.payload as EffectivenessAndEfficiencyData;

                return (
                  // custom bar chart colors
                  <Rectangle
                    {...shapeProps}
                    id={`benchmarking-dashboard__effectiveness-and-efficiency--contract-${entry.contractId}-${props.chartType}-${entry[props.chartType as keyof EffectivenessAndEfficiencyData]}`}
                    fill={entry.contractId === props.selectedNetwork ? colors.green : colors.blue}
                    cursor="pointer"
                  />
                );
              }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </DashboardTile>
  )
}

export default EffectivenessAndEfficiency;
